import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import _get from "lodash/get";
import _noop from "lodash/noop";
import _debounce from "lodash/debounce";

import {
  getComponents,
  getInputLabelProps,
  isOptionDisabled,
  LazyMenuList,
} from "../AutocompleteSelect/AutocompleteSelect";
import InputLoader from "../../../ui/Loader/InputLoader";
import { DropdownIndicator, StyleSelected } from "./components";
import {
  PORevisionContext,
  RevisionReferenceContext,
} from "../../../../withPORevision";
import {
  optionsWithEmptyValue,
  getLabel,
  getNullAsEmptyValue,
} from "../StandardSelect";
import { useLabeledText } from "../../TextInputWithError";

const getValue = ({ menuItemProps, ...data }, submitData, valueProperty) => {
  return (submitData ? data : data[valueProperty]) || null;
};

export const buildChangeHandler = (
  submitData,
  name,
  onChange,
  valueProperty = "id",
  onSelectedItem,
  selectRef,
  handleChange,
  nullAsEmpty
) => data => {
  const value = getValue(data, submitData, valueProperty);
  const event = {
    type: "click",
    target: { name, value: getNullAsEmptyValue({ value, nullAsEmpty }) },
  };
  onChange(event);
  handleChange && handleChange(event);
  onSelectedItem && onSelectedItem(data);
  setTimeout(() => {
    if (selectRef.current?.select) {
      selectRef.current.select.focus();
    }
  });
};

const checkWasModified = (
  isActive,
  isUpdated,
  hasModifiedChildrens,
  reference,
  name
) => () => {
  if (!isActive) return false;
  return isUpdated(reference, name);
};

const getSelected = (valueProperty, value, submitData) => option => {
  const optionValue = _get(option, valueProperty);
  return submitData
    ? optionValue === _get(value, valueProperty)
    : optionValue === value;
};

const findScrollableAncestor = el => {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return null;
};

const useScrollLock = selectRef => {
  const scrollLockRef = useRef(null);

  useEffect(() => {
    return () => {
      if (scrollLockRef.current) {
        scrollLockRef.current.style.overflowY = "";
      }
    };
  }, []);

  const lockScroll = useCallback(() => {
    if (scrollLockRef.current) return;
    const controlEl =
      selectRef.current?.select?.controlRef || selectRef.current?.controlRef;
    const target = findScrollableAncestor(controlEl);
    if (target) {
      scrollLockRef.current = target;
      target.style.overflowY = "hidden";
    }
  }, [selectRef]);

  const unlockScroll = useCallback(() => {
    if (scrollLockRef.current) {
      scrollLockRef.current.style.overflowY = "";
      scrollLockRef.current = null;
    }
  }, []);

  return { lockScroll, unlockScroll };
};

const SingleAutoCompleteSelect = ({
  isDisabled,
  isLoading,
  isClearable,
  isSearchable,
  onInputChange,
  onChange,
  options: initialOptions,
  valueProperty,
  labelProperty,
  label,
  value,
  error,
  helperText,
  textFieldProps,
  onSelectedItem,
  onMenuOpen,
  onScrollNearBottom,
  nullAsEmpty,
  displayEmpty,
  handleChange,
  ignoreLabeledText,
  defaultOption,
  searchPending,
  ...props
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { isActive, isUpdated, hasModifiedChildrens } = useContext(
    PORevisionContext
  );
  const labeledText = useLabeledText(ignoreLabeledText);
  const selectRef = useRef();
  const { lockScroll, unlockScroll } = useScrollLock(selectRef);
  const { reference } = useContext(RevisionReferenceContext);
  const [isOpen, setIsOpen] = useState(false);

  const onChangeDebounced = _debounce(onChange, 300);

  const options = useMemo(
    optionsWithEmptyValue(
      valueProperty,
      labelProperty,
      initialOptions,
      displayEmpty,
      nullAsEmpty,
      defaultOption
    ),
    [valueProperty, labelProperty, initialOptions, displayEmpty, nullAsEmpty]
  );

  const onScrollNearBottomRef = useRef(onScrollNearBottom);
  useEffect(() => {
    onScrollNearBottomRef.current = onScrollNearBottom;
  }, [onScrollNearBottom]);

  const components = useMemo(() => {
    const base = { ...getComponents(label), DropdownIndicator };
    if (!onScrollNearBottom) return base;
    const LazyMenuListWrapper = menuListProps => (
      <LazyMenuList
        {...menuListProps}
        onScrollNearBottom={(...args) =>
          onScrollNearBottomRef.current?.(...args)
        }
      />
    );
    LazyMenuListWrapper.displayName = "LazyMenuListWrapper";
    return { ...base, MenuList: LazyMenuListWrapper };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, !!onScrollNearBottom]);

  const selected = options.find(
    getSelected(valueProperty, value, props.submitData)
  );

  const wasModified = useMemo(
    checkWasModified(
      isActive,
      isUpdated,
      hasModifiedChildrens,
      reference,
      props.name
    ),
    [isActive, isUpdated, hasModifiedChildrens, reference, props.name]
  );

  const handleMenuOpen = useCallback(() => {
    if (!isOpen) {
      setIsOpen(true);
      onMenuOpen("");
      lockScroll();
    }
  }, [isOpen, onMenuOpen, lockScroll]);

  const handleMenuClose = useCallback(() => {
    setIsOpen(false);
    unlockScroll();
  }, [unlockScroll]);

  const inputProps = {
    textFieldProps,
    helperText,
    error,
    label,
  };
  const showLoading = isLoading || searchPending;
  return (
    <InputLoader>
      <StyleSelected
        {...props}
        selectRef={selectRef}
        value={selected || null}
        textFieldProps={getInputLabelProps(value, inputProps, wasModified)}
        getOptionValue={data => {
          return data[valueProperty];
        }}
        getOptionLabel={data => {
          return getLabel(labelProperty, data);
        }}
        onInputChange={onInputChange}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        components={components}
        onChange={buildChangeHandler(
          props.submitData,
          props.name,
          onChangeDebounced,
          valueProperty,
          onSelectedItem,
          selectRef,
          handleChange,
          nullAsEmpty
        )}
        isDisabled={isDisabled || labeledText}
        isLoading={showLoading}
        options={showLoading && !onScrollNearBottom ? [] : options}
        isOptionDisabled={isOptionDisabled}
      />
    </InputLoader>
  );
};

SingleAutoCompleteSelect.defaultProps = {
  valueProperty: "id",
  labelProperty: "name",
  onMenuOpen: _noop,
};

export default SingleAutoCompleteSelect;
